import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef } from 'ag-grid-community';
import { DeviceMonitoringModalLinkComponent } from 'src/app/monitoring/device-monitoring-modal-link/device-monitoring-modal-link.component';
import { ReleaseViewModalComponent } from 'src/app/releases/release-view-modal/release-view-modal.component';
import {
  AppVersion,
  DeviceVersionsQuery,
  VersionsService,
} from 'src/app/shared/services/versions/versions.service';
import { CohortsModalRendererComponent } from './cohorts-modal-link-renderer/cohorts-modal-renderer.component';
import { ModuleStatusComponent } from './module-status/module-status.component';

@Component({
  selector: 'dcc-modules-insights',
  templateUrl: './modules-insights.component.html',
  styleUrls: ['./modules-insights.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModulesInsightsComponent implements OnInit {
  apps: AppVersion[] = [];
  pageNumber = 1;
  pageCount = 1;
  totalDevices = 0;
  pageSize = 40;
  isLoading = false;

  defaultColumnConfiguration: ColDef = {
    sortable: false,
    resizable: false,
    flex: 1,
  };

  columnDefinitions: ColDef<AppVersion>[] = [
    {
      headerName: 'Serial Number',
      field: 'serialNumber',
      headerTooltip: 'Serial Number of the device',
      cellRenderer: DeviceMonitoringModalLinkComponent,
    },
    {
      headerName: 'Apps',
      field: 'name',
      headerTooltip: 'Name of the module',
    },
    {
      headerName: 'Status',
      field: 'status',
      headerTooltip: 'Installation status of the module',
      cellRenderer: ModuleStatusComponent,
    },
    {
      headerName: 'Version',
      field: 'version',
      headerTooltip: 'Module version currently installed on device',
    },
    {
      headerName: 'Expected Version',
      headerTooltip: 'Module version expected on device',
      cellRenderer: this.firmwareExpectedVersionRenderer,
    },
    {
      headerName: 'Last Versions Sync',
      headerTooltip: 'Date of the last version update',
      valueFormatter: (params) =>
        params.data.lastVersionsDate
          ? new Date(params.data.lastVersionsDate).toLocaleDateString()
          : '',
    },
    {
      headerName: 'Install Date',
      headerTooltip: 'Date of the last version update',
      valueFormatter: (params) =>
        params.data.installDate
          ? new Date(params.data.installDate).toLocaleDateString()
          : '',
    },
    {
      headerName: 'Release',
      headerTooltip: 'Relase that effects expected version',
      cellRenderer: this.releaseLinkRederer,
    },
    {
      headerName: 'Cohorts',
      headerTooltip: 'Cohorts from the expected release',
      cellRenderer: CohortsModalRendererComponent,
    },
    //{ headerName: 'Note', field: '' } Not implemented yet
  ];

  private query: DeviceVersionsQuery = {};

  constructor(
    private versionsService: VersionsService,
    private changes: ChangeDetectorRef,
    private modalService: NgbModal,
  ) {}

  releaseLinkRederer(params: any): HTMLElement {
    if (
      params.data.releaseId &&
      params.data.releaseId !== '00000000-0000-0000-0000-000000000000'
    ) {
      const link = document.createElement('a');
      link.href = '#';
      link.textContent = params.data.releaseId;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const modal = this.modalService.open(ReleaseViewModalComponent, {
          size: 'lg',
          windowClass: 'lytxModal',
          centered: true,
        });
        modal.componentInstance.releaseId = params.data.releaseId;
      });
      return link;
    }
    const NoReleaseText = document.createElement('span');
    NoReleaseText.textContent = params.value;
    return NoReleaseText;
  }

  ngOnInit() {
    this.getData();
  }

  onQuery(query: DeviceVersionsQuery) {
    this.query = query;
    this.resetPagination();
    this.getData();
  }

  onPaginate() {
    this.pageNumber++;
    this.getData();
  }

  onClickFirst() {
    this.resetPagination();
    this.getData();
  }

  private async getData() {
    this.isLoading = true;
    try {
      const response = await this.versionsService.getAppVersions({
        ...this.query,
        size: 40,
        usePagination: true,
      });

      this.totalDevices = response.totalCount;

      this.pageCount = Math.ceil(this.totalDevices / this.pageSize);

      this.apps = response.appVersions;

      this.apps = this.apps.sort((a, b) => {
        if (a.serialNumber === b.serialNumber) {
          if (a.name === 'Firmware' && b.name !== 'Firmware') {
            return -1;
          }
          if (a.name !== 'Firmware' && b.name === 'Firmware') {
            return 1;
          }
          return 0;
        }
        return a.serialNumber.localeCompare(b.serialNumber);
      });

      this.isLoading = false;

      this.query.searchAfter = response.searchAfter;

      this.changes.markForCheck();
    } catch (error) {
      console.error('Error fetching app versions', error);
      this.isLoading = false;
      this.changes.detectChanges();
    }
  }

  private firmwareExpectedVersionRenderer(params: any) {
    if (params.data.name === 'Firmware' && params.data.expectedFirmwareName) {
      return (
        params.data.expectedFirmwareName + ' > ' + params.data.expectedVersion
      );
    }
    return params.data.expectedVersion;
  }

  private resetSearchAfterSortValues() {
    this.query.searchAfter = null;
  }

  private resetPagination() {
    this.pageNumber = 1;
    this.resetSearchAfterSortValues();
  }
}
